import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Banner, Button, IconButton, Input, SectionLabel } from '../../design/components'
import { useEncounterCandidates, useEncounterSheet } from '../encounters/queries'
import { useEncounterEditing } from '../encounters/useEncounterEditing'
import { LocationPicker } from '../encounters/sections/LocationPicker'
import { liveKeys } from './queries'
import type { EncounterType, LiveSession } from './types'
import styles from './live.module.css'

export interface EncounterTabProps {
  session: LiveSession
}

/**
 * The encounter itself, edited from inside the session.
 *
 * **This is the tab the rework exists for.** An encounter used to be arranged before a session and
 * frozen for its duration: to re-type it, move it, or add the person who just walked in, a Game
 * Master ended the session, went to the encounter screen, and started a new one — losing the map,
 * the order and everyone's connection to do it. Now the encounter is editable where it is being
 * used.
 *
 * **It calls the ordinary encounter endpoints**, not a parallel set on the live controller
 * (`docs/web-api/phase-10-kickoff.md` §1, decision 2). Those endpoints already enforce the Game
 * Master rule against the database, and every one of them ends by syncing whatever session is
 * running — so the change reaches every screen at the table by the same push a token move does,
 * without this component knowing that. What this file has to do afterwards is exactly one thing:
 * ask for the session again in case the hub is down, so a Game Master on a dead socket still sees
 * their own edit land.
 *
 * The controls are flat rather than the encounter screen's cards. This lives in a tool column
 * beside a map, next to a combat card that is the thing actually being read — a stack of nested
 * cards here would push that card off the screen, which is the same reasoning that made these
 * tools tabbed in the first place.
 */
export function EncounterTab({ session }: EncounterTabProps) {
  const queryClient = useQueryClient()

  const sheet = useEncounterSheet(session.encounterId)
  const editing = useEncounterEditing(session.encounterId)

  const core = sheet.data?.sections.core
  const sides = sheet.data?.sections.sides?.sides ?? []

  const [name, setName] = useState(session.name)
  const [renaming, setRenaming] = useState(false)

  // The session is the live truth for the name — a rename made in another tab arrives over the
  // hub, and the field it would edit has to follow rather than sit on a stale value.
  useEffect(() => setName(session.name), [session.name])

  /**
   * Every encounter write here goes through one shape.
   *
   * `name` and `notes` are re-sent on every call because `PUT /encounters/{id}` is a replace
   * rather than a patch for those two fields — omitting the notes would blank them. The location
   * is genuinely three-state and is left alone unless a caller says otherwise.
   */
  const write = (patch: { name?: string; type?: EncounterType; locationId?: number | null }) =>
    editing.update.mutate(
      {
        name: patch.name ?? core?.name ?? session.name,
        notes: (core?.notes ?? '') === '' ? null : (core?.notes ?? null),
        ...(patch.type === undefined ? {} : { type: patch.type }),
        ...(patch.locationId === undefined
          ? {}
          : patch.locationId === null
            ? { clearLocation: true }
            : { locationId: patch.locationId }),
      },
      { onSuccess: refreshSession },
    )

  /**
   * Re-reads the session after an encounter write.
   *
   * The server has already pushed the new session to everyone watching, so on a healthy hub this
   * is a no-op that costs one request. On a dead one it is the difference between the Game Master
   * seeing the map they just chose and staring at the old screen wondering whether the click took.
   */
  function refreshSession() {
    void queryClient.invalidateQueries({ queryKey: liveKeys.session(session.sessionId) })
  }

  return (
    <>
      {editing.failure !== null && (
        <Banner
          tone="error"
          actions={
            <Button variant="secondary" onClick={editing.dismiss}>
              Dismiss
            </Button>
          }
        >
          {editing.failure}
        </Banner>
      )}

      {/* ----- what it is ----- */}

      {renaming ? (
        <form
          className={styles.inlineEdit}
          onSubmit={(event) => {
            event.preventDefault()
            if (name.trim() === '') return
            write({ name: name.trim() })
            setRenaming(false)
          }}
        >
          <Input
            label="Name"
            value={name}
            autoFocus
            onChange={(event) => setName(event.target.value)}
          />
          <div className={styles.glyphRow}>
            <Button type="submit" disabled={editing.update.isPending || name.trim() === ''}>
              Save
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setName(session.name)
                setRenaming(false)
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div className={styles.listRow}>
          <span>{session.name}</span>
          <IconButton label="Rename this encounter" icon="edit" onClick={() => setRenaming(true)} />
        </div>
      )}

      <SectionLabel tone="muted" micro>
        What kind of encounter
      </SectionLabel>
      {/* Two buttons rather than a select: there are exactly two answers, both are one word, and
          the current one has to be readable at a glance from across a table. Switching re-reads
          every card on every screen through the other lens — the combat numbers become the social
          ones — which is the whole reason this control is here rather than on a settings page. */}
      <div className={styles.glyphRow}>
        {(['Combat', 'Social'] as const).map((type) => (
          <Button
            key={type}
            variant={session.encounterType === type ? 'primary' : 'secondary'}
            disabled={editing.update.isPending || session.encounterType === type}
            onClick={() => write({ type })}
          >
            {type}
          </Button>
        ))}
      </div>

      {/* ----- where it happens, which is also where the map comes from ----- */}

      <LocationPicker
        campaignId={session.campaignId}
        value={session.locationId}
        disabled={editing.update.isPending}
        onChange={(next) => write({ locationId: next })}
        hint={
          session.mapImageUrl === null
            ? 'A place with a battle map puts one on everyone’s screen. Without one, the encounter runs on faces alone.'
            : undefined
        }
      />

      {/* ----- who is in it ----- */}

      <SectionLabel tone="muted" micro>
        Sides
      </SectionLabel>

      {sheet.isPending ? (
        <p className={styles.note}>Reading the encounter…</p>
      ) : sheet.isError ? (
        <p className={styles.note}>Could not read the encounter.</p>
      ) : (
        <>
          {sides.map((side) => (
            <SideBlock
              key={side.id}
              encounterId={session.encounterId}
              side={side}
              editing={editing}
              onWritten={refreshSession}
            />
          ))}

          <AddSide editing={editing} onWritten={refreshSession} />
        </>
      )}
    </>
  )
}

/** One side: who stands on it, and the two ways that changes. */
function SideBlock({
  encounterId,
  side,
  editing,
  onWritten,
}: {
  encounterId: number
  side: { id: number; sideName: string; characters: { characterId: number; name: string }[] }
  editing: ReturnType<typeof useEncounterEditing>
  onWritten: () => void
}) {
  const [adding, setAdding] = useState(false)

  return (
    <div className={styles.sideBlock}>
      <div className={styles.listRow}>
        <span className={styles.sideName}>{side.sideName}</span>
        <span className={styles.glyphRow}>
          <IconButton
            label={`Add someone to ${side.sideName}`}
            icon="add"
            onClick={() => setAdding((open) => !open)}
          />
          <IconButton
            label={`Remove the side ${side.sideName}`}
            icon="delete"
            disabled={editing.removeSide.isPending}
            onClick={() => editing.removeSide.mutate({ sideId: side.id }, { onSuccess: onWritten })}
          />
        </span>
      </div>

      {side.characters.length === 0 ? (
        <p className={styles.note}>Nobody on this side.</p>
      ) : (
        side.characters.map((entry) => (
          <div key={entry.characterId} className={styles.listRow}>
            <span className={styles.rowMeta}>{entry.name}</span>
            <IconButton
              label={`Take ${entry.name} out of the encounter`}
              icon="delete"
              disabled={editing.removeCharacter.isPending}
              onClick={() =>
                editing.removeCharacter.mutate(
                  { sideId: side.id, characterId: entry.characterId },
                  { onSuccess: onWritten },
                )
              }
            />
          </div>
        ))
      )}

      {adding && (
        <AddToSide
          encounterId={encounterId}
          sideId={side.id}
          editing={editing}
          onWritten={onWritten}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  )
}

/**
 * The roster, minus everyone already in the encounter.
 *
 * Server-filtered, as on the encounter screen: a character stands on one side or none, and the API
 * decides that rather than this picker.
 */
function AddToSide({
  encounterId,
  sideId,
  editing,
  onWritten,
  onClose,
}: {
  encounterId: number
  sideId: number
  editing: ReturnType<typeof useEncounterEditing>
  onWritten: () => void
  onClose: () => void
}) {
  const candidates = useEncounterCandidates(encounterId)
  const found = candidates.data ?? []

  return (
    <div className={styles.tray}>
      {candidates.isPending && <p className={styles.note}>Reading the roster…</p>}

      {candidates.isSuccess && found.length === 0 && (
        <p className={styles.note}>Everyone on the roster is already in this encounter.</p>
      )}

      {found.map((candidate) => (
        <button
          key={candidate.characterId}
          type="button"
          className={styles.trayItem}
          disabled={editing.addCharacter.isPending}
          onClick={() =>
            editing.addCharacter.mutate(
              { sideId, characterId: candidate.characterId ?? 0 },
              {
                onSuccess: () => {
                  onWritten()
                  onClose()
                },
              },
            )
          }
        >
          {candidate.portraitUrl != null && (
            <img className={styles.trayThumb} src={candidate.portraitUrl} alt="" />
          )}
          {candidate.name}
        </button>
      ))}
    </div>
  )
}

function AddSide({
  editing,
  onWritten,
}: {
  editing: ReturnType<typeof useEncounterEditing>
  onWritten: () => void
}) {
  const [sideName, setSideName] = useState('')

  return (
    <form
      className={styles.tray}
      onSubmit={(event) => {
        event.preventDefault()
        if (sideName.trim() === '') return

        editing.addSide.mutate(
          { sideName: sideName.trim() },
          {
            onSuccess: () => {
              setSideName('')
              onWritten()
            },
          },
        )
      }}
    >
      <input
        className={styles.trayItem}
        value={sideName}
        placeholder="Another side"
        maxLength={100}
        aria-label="Name for a new side"
        onChange={(event) => setSideName(event.target.value)}
      />
      <Button type="submit" disabled={editing.addSide.isPending || sideName.trim() === ''}>
        Add
      </Button>
    </form>
  )
}
